import React, { createContext, useContext, useCallback } from 'react';
import { Excel } from 'antd-table-saveas-excel';
import { filter } from 'lodash';
import { message } from 'antd';
import { ProColumns } from '@ant-design/pro-table';
import { ExcelDefaultTheadCellStyle } from '@/common/utils';
import { ExcelDefaultTbodyCellStyle } from '@/common/utils';
import { useIntl } from 'umi';
import { loadExcelColumns } from '@/utils';

interface DownloadContextType {
  downloadExcel: <T = any>(params: {
    columns: ProColumns<T>[];
    getDataFn: () => Promise<T[]>;
    filename: string;
    columnsStateKey?: string;
  }) => Promise<void>;
}

const DownloadContext = createContext<DownloadContextType | null>(null);

export const useDownload = () => {
  const context = useContext(DownloadContext);
  if (!context) {
    throw new Error('useDownload must be used within a DownloadProvider');
  }
  return context;
};

export const DownloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const downloadExcel = useCallback(
    async <T extends unknown>({
      columns,
      getDataFn,
      filename,
      columnsStateKey,
    }: {
      columns: ProColumns<T>[];
      getDataFn: () => Promise<T[]>;
      filename: string;
      columnsStateKey?: string;
    }) => {
      try {
        // 获取数据
        const data = await getDataFn();

        if (!data.length) {
          message.warning(intl.formatMessage({ id: 'no_data_to_download' }));
          return;
        }

        const exportColumns = columnsStateKey
          ? loadExcelColumns(columns, columnsStateKey)
          : columns;

        // 清理 columns 数据，只保留必要的字段
        const cleanColumns = exportColumns.map(col => ({
          dataIndex: col.dataIndex,
          title: typeof col.title === 'string' ? col.title : '',
          hideInTable: col.hideInTable,
          excelRender: col.excelRender,
          __style__: col.__style__,
          __numFmt__: col.__numFmt__,
          __cellType__: col.__cellType__,
          __ignore__: col.__ignore__,
          __excelTitle__: col.__excelTitle__,
        }));

        // 生成Excel
        const excel = new Excel();
        excel.defaultTheadCellStyle = ExcelDefaultTheadCellStyle;
        excel.defaultTbodyCellStyle = ExcelDefaultTbodyCellStyle;
        excel
          .addSheet('Sheet 1')
          .addColumns(filter(cleanColumns, c => !c.hideInTable))
          .addDataSource(data, {
            str2Percent: true,
          })
          .saveAs(filename);
      } catch (error) {
        console.error('Download error:', error);
        message.error('下载失败');
      }
    },
    [intl],
  );

  return <DownloadContext.Provider value={{ downloadExcel }}>{children}</DownloadContext.Provider>;
};

export default DownloadProvider;
